import Spinner from '../ui/spinner';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users,
  User,
  MessageCircle,
  TrendingUp,
  Activity,
  ArrowLeft,
  Mail,
  Shield,
  SquarePen,
  VolumeX,
  Volume2,
  Trash2,
  Flag,
  CheckCircle,
  AlertTriangle,
  Settings,
} from 'lucide-react';

import { useTranslation } from '../../i18n';
import {
  useUserDetail,
  useUnmuteUser,
  useUnbanUser,
} from '../../hooks/queries/useUserQueries';
import {
  useUserFriends,
  useUserBlocks,
} from '../../hooks/queries/useFriendQueries';
import useModal from '../../hooks/useModal';
import { getImageUrl } from '../../utils/imageUrl';
import { formatDate } from '../../utils/formatDate';
import {
  getReasonLabel as getReasonLabelUtil,
  getTypeLabel as getTypeLabelUtil,
} from '../../utils/reportLabels';
import { IUser } from '../../types';
import Pagination from '../Pagination';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import UserFormDialog from './UserFormDialog';
import UserNotifyDialog from './UserNotifyDialog';
import UserMuteDialog from './UserMuteDialog';
import UserBanDialog from './UserBanDialog';
import UserDeleteDialog from './UserDeleteDialog';

const AVATAR_COLOR = '#3b82f6';
const PAGE_LIMIT = 20;
const ROLE_STYLES: Record<string, string> = {
  owner: 'bg-[#F3E8FF] text-[#7C3AED]',
  admin: 'bg-[#CCFBF1] text-[#0F766E]',
  member: 'bg-gray-100 text-gray-500',
};

const STATUS_CONFIG: Record<
  string,
  { card: string; icon: string; title: string; desc: string; date: string }
> = {
  pending: {
    card: 'border-red-200 bg-red-50',
    icon: 'text-red-600',
    title: 'text-red-900',
    desc: 'text-red-700',
    date: 'text-red-600',
  },
  reviewing: {
    card: 'border-orange-200 bg-orange-50',
    icon: 'text-orange-600',
    title: 'text-orange-900',
    desc: 'text-orange-700',
    date: 'text-orange-600',
  },
  resolved: {
    card: 'border-green-200 bg-green-50',
    icon: 'text-green-600',
    title: 'text-green-900',
    desc: 'text-green-700',
    date: 'text-green-600',
  },
};

const STATUS_ICONS: Record<string, typeof Flag> = {
  pending: Flag,
  reviewing: AlertTriangle,
  resolved: CheckCircle,
};

const UserDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const getReasonLabel = (reason: string) => getReasonLabelUtil(reason, t);
  const getTypeLabel = (type: string) => getTypeLabelUtil(type, t);
  const [detailTab, setDetailTab] = useState<
    'groups' | 'reports' | 'friends' | 'blocks'
  >('groups');
  const [friendsPage, setFriendsPage] = useState(1);
  const [blocksPage, setBlocksPage] = useState(1);
  const { data: userDetail, isLoading, refetch } = useUserDetail(id || '');
  const { data: friendsData } = useUserFriends(
    id || '',
    friendsPage,
    PAGE_LIMIT,
  );
  const { data: blocksData } = useUserBlocks(id || '', blocksPage, PAGE_LIMIT);

  const editModal = useModal<IUser>();
  const notifyModal = useModal<IUser>();
  const muteModal = useModal<IUser>();
  const banModal = useModal<IUser>();
  const deleteModal = useModal<IUser>();

  const unmuteUser = useUnmuteUser();
  const unbanUser = useUnbanUser();

  if (isLoading || !userDetail) {
    return <Spinner />;
  }

  const handleBack = () => navigate('/users');
  const handleNavigateRoom = (roomId: string) =>
    navigate(`/room-detail/${roomId}`);

  const roleLabels: Record<string, string> = {
    member: t('users.member'),
    space_owner: t('users.space_owner'),
    space_admin: t('users.space_admin'),
  };
  const roleLabelMap: Record<string, string> = {
    owner: t('users.role_owner'),
    admin: t('users.role_admin'),
    member: t('users.role_member'),
  };
  const statusBadges: Record<string, { cls: string; label: string }> = {
    pending: {
      cls: 'bg-yellow-100 text-yellow-700',
      label: t('users.report_status_pending'),
    },
    reviewing: {
      cls: 'bg-blue-100 text-blue-700',
      label: t('users.report_status_reviewing'),
    },
    resolved: {
      cls: 'bg-green-100 text-green-700',
      label: t('users.report_status_resolved'),
    },
    rejected: {
      cls: 'bg-gray-100 text-gray-700',
      label: t('users.report_status_rejected'),
    },
  };

  const stats = [
    {
      label: t('users.rooms_joined'),
      value: userDetail.stats?.roomCount?.toString() || '—',
      Icon: Users,
      color: 'text-blue-600',
    },
    {
      label: t('users.message_count'),
      value: userDetail.stats?.messageCount?.toLocaleString() || '—',
      Icon: MessageCircle,
      color: 'text-green-600',
    },
    {
      label: t('users.daily_avg_messages'),
      value: userDetail.stats?.dailyAvgMessages?.toString() || '—',
      Icon: TrendingUp,
      color: 'text-purple-600',
    },
    {
      label: t('users.total_logins'),
      value: userDetail.stats?.loginCount?.toString() || '—',
      Icon: Activity,
      color: 'text-orange-600',
    },
  ];

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-8">
      <button
        onClick={handleBack}
        className="flex items-center gap-1 text-sm text-[#0A0A0A] hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" /> {t('users.back_to_list')}
      </button>

      {/* User header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl shrink-0 overflow-hidden"
            style={{
              background: userDetail.avatar ? 'transparent' : AVATAR_COLOR,
            }}
          >
            {userDetail.avatar ? (
              <img
                src={getImageUrl(userDetail.avatar)!}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              (userDetail.displayName || userDetail.username)
                .charAt(0)
                .toUpperCase()
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {userDetail.displayName}
              </h1>
              <Badge variant={userDetail.isActive ? 'success' : 'danger'}>
                {userDetail.isActive ? t('common.active') : t('users.banned')}
              </Badge>
              {userDetail.isMuted && (
                <Badge variant="warning">{t('users.muted')}</Badge>
              )}
              <Badge variant="purple">
                {roleLabels[userDetail.role] || userDetail.role}
              </Badge>
            </div>
            <p className="text-sm text-gray-400 mt-0.5">
              <User className="w-4 h-4 inline" /> @{userDetail.username}
            </p>
            <p className="text-sm text-gray-500 mt-0.5">{t('users.bio')}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => editModal.open(userDetail as IUser)}
          >
            <SquarePen className="w-4 h-4" />
            {t('users.edit_user')}
          </Button>
          <Button
            variant="outline"
            onClick={() => notifyModal.open(userDetail as IUser)}
          >
            <Mail className="w-4 h-4" /> {t('users.send_notification')}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              if (userDetail.isMuted) {
                unmuteUser.mutateAsync(userDetail.id).then(() => refetch());
              } else {
                muteModal.open(userDetail as IUser);
              }
            }}
            className={
              userDetail.isMuted
                ? 'border-green-200 text-green-600 hover:bg-green-50'
                : 'border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50'
            }
          >
            {userDetail.isMuted ? (
              <Volume2 className="w-4 h-4" />
            ) : (
              <VolumeX className="w-4 h-4" />
            )}
            {userDetail.isMuted ? t('users.unmute_user') : t('users.mute_user')}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              if (userDetail.isActive) {
                banModal.open(userDetail as IUser);
              } else {
                unbanUser.mutateAsync(userDetail.id).then(() => refetch());
              }
            }}
            className={
              userDetail.isActive
                ? 'border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50'
                : 'border-green-200 text-green-600 hover:bg-green-50'
            }
          >
            <Shield className="w-4 h-4" />{' '}
            {userDetail.isActive ? t('users.ban_user') : t('users.unban_user')}
          </Button>
          <Button
            variant="outline"
            onClick={() => deleteModal.open(userDetail as IUser)}
            className="border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" /> {t('users.delete_account')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, Icon, color }) => (
          <Card key={label}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">
                    {label}
                  </div>
                  <div className="text-3xl font-bold text-foreground">
                    {value}
                  </div>
                </div>
                <Icon className={`w-8 h-8 ${color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info + Groups/Reports */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* User info card */}
        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-5">
            <Settings className="w-5 h-5 text-gray-700" />
            <h3 className="text-base font-semibold text-gray-900">
              {t('users.user_info')}
            </h3>
          </div>

          {/* Phone number */}
          <div className="mb-5">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-1">
              <User className="w-4 h-4" />
              {t('users.phone_number')}
            </div>
            <div className="text-sm text-gray-900 pl-5.5">
              {userDetail.mobileNumber
                ? `${(userDetail as any).countryCode ? `+${(userDetail as any).countryCode} ` : ''}${userDetail.mobileNumber}`
                : '—'}
            </div>
          </div>

          {/* Email */}
          <div className="mb-5">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-1">
              <User className="w-4 h-4" />
              {t('users.email')}
            </div>
            <div className="text-sm text-gray-900 pl-5.5">
              {(userDetail as any).email || '—'}
            </div>
          </div>

          <div className="border-t border-border pt-5 mb-5">
            <div className="text-xs text-gray-400 mb-3">
              {t('users.group_roles')}
            </div>
            <div className="space-y-2">
              {(userDetail as any).spaceMemberships?.length > 0 ? (
                (userDetail as any).spaceMemberships.map((sm: any) => (
                  <div
                    key={sm.id}
                    className="flex items-center justify-between py-2 px-3 rounded-lg border border-border"
                  >
                    <span className="text-sm text-gray-900">
                      {sm.spaceName}
                    </span>
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap shrink-0 ${ROLE_STYLES[sm.role] || 'bg-gray-100 text-gray-600'}`}
                    >
                      {roleLabelMap[sm.role] || sm.role}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-400">—</div>
              )}
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {t('users.join_date')}
              </span>
              <span className="text-sm text-gray-900">
                {formatDate(userDetail.createdAt)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {t('users.last_active')}
              </span>
              <span className="text-sm text-gray-900">
                {(userDetail as any).lastActiveAt
                  ? formatDate((userDetail as any).lastActiveAt)
                  : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Groups + Reports */}
        <div className="col-span-2 bg-white rounded-xl border border-border p-6">
          <div className="flex gap-1 mb-5 bg-[#ECECF0] rounded-lg p-1">
            <button
              onClick={() => setDetailTab('groups')}
              className={`flex-1 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${detailTab === 'groups' ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-[#0A0A0A]'}`}
            >
              {t('users.rooms_participated')}
            </button>
            <button
              onClick={() => setDetailTab('reports')}
              className={`flex-1 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${detailTab === 'reports' ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-[#0A0A0A]'}`}
            >
              {t('users.report_history')}
            </button>
            <button
              onClick={() => setDetailTab('friends')}
              className={`flex-1 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${detailTab === 'friends' ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-[#0A0A0A]'}`}
            >
              {t('users.friends')}
            </button>
            <button
              onClick={() => setDetailTab('blocks')}
              className={`flex-1 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${detailTab === 'blocks' ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-[#0A0A0A]'}`}
            >
              {t('users.blocked')}
            </button>
          </div>

          {detailTab === 'groups' && (
            <div className="space-y-3">
              {userDetail.rooms?.length > 0 ? (
                userDetail.rooms.map((room) => (
                  <button
                    key={room.id}
                    type="button"
                    aria-label={t('common.view_room_detail')}
                    onClick={() => handleNavigateRoom(room.id)}
                    className="w-full text-left flex items-center justify-between p-3 rounded-xl border border-border hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#A855F7] flex items-center justify-center shrink-0">
                        <MessageCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {room.name}
                        </div>
                        <div className="text-xs text-gray-400">
                          {room.spaceId
                            ? t('users.group_room')
                            : t('users.direct_message')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" /> {room.memberCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-4 h-4" />{' '}
                        {room.messageCount}
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-gray-400 py-8 text-center">
                  {t('users.no_rooms')}
                </p>
              )}
            </div>
          )}

          {detailTab === 'reports' && (
            <div className="space-y-2">
              {(userDetail as any).reports?.length > 0 ? (
                (userDetail as any).reports.map((report: any) => {
                  const cfg =
                    STATUS_CONFIG[report.status] || STATUS_CONFIG.resolved;
                  const IconComp = STATUS_ICONS[report.status] || CheckCircle;
                  const badge =
                    statusBadges[report.status] || statusBadges.pending;

                  return (
                    <div
                      key={report.id}
                      className={`flex items-start gap-3 p-3 border rounded-lg ${cfg.card}`}
                    >
                      <IconComp
                        className={`w-4 h-4 mt-0.5 shrink-0 ${cfg.icon}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className={`text-xs font-medium ${cfg.title}`}>
                              {t('users.reported_for', {
                                type: getTypeLabel(report.type),
                              })}
                            </p>
                            <p className={`text-xs mt-1 ${cfg.desc}`}>
                              {report.roomName
                                ? t('users.report_in_room', {
                                    room: report.roomName,
                                  })
                                : getReasonLabel(report.reason)}
                            </p>
                          </div>
                          <span
                            className={`text-xs whitespace-nowrap ${cfg.date}`}
                          >
                            {formatDate(report.createdAt)}
                          </span>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium mt-1 ${badge.cls}`}
                        >
                          {badge.label}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-400 py-8 text-center">
                  {t('users.no_reports')}
                </p>
              )}
            </div>
          )}

          {detailTab === 'friends' && (
            <div className="space-y-3">
              {friendsData?.data && friendsData.data.length > 0 ? (
                friendsData.data.map((friend) => (
                  <div
                    key={friend.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-border hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        {friend.avatar ? (
                          <img
                            src={getImageUrl(friend.avatar)}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {friend.displayName || friend.username}
                        </div>
                        <div className="text-xs text-gray-400">
                          @{friend.username}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-gray-500">
                        {friend.friendsSince && formatDate(friend.friendsSince)}
                      </div>
                      {friend.roomId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate(`/room-messages/${friend.roomId}`)
                          }
                          title={t('users.view_chat')}
                        >
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 py-8 text-center">
                  {t('users.no_friends')}
                </p>
              )}

              {friendsData && (
                <div className="-mx-6 -mb-6">
                  <Pagination
                    page={friendsData.page}
                    totalPages={friendsData.totalPages}
                    total={friendsData.total}
                    limit={PAGE_LIMIT}
                    onPageChange={setFriendsPage}
                  />
                </div>
              )}
            </div>
          )}

          {detailTab === 'blocks' && (
            <div className="space-y-3">
              {blocksData?.data && blocksData.data.length > 0 ? (
                blocksData.data.map((block) => (
                  <div
                    key={block.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-border hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                        {block.avatar ? (
                          <img
                            src={getImageUrl(block.avatar)}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <Shield className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {block.displayName || block.username}
                        </div>
                        <div className="text-xs text-gray-400">
                          @{block.username}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {block.blockedAt && formatDate(block.blockedAt)}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 py-8 text-center">
                  {t('users.no_blocks')}
                </p>
              )}

              {blocksData && (
                <div className="-mx-6 -mb-6">
                  <Pagination
                    page={blocksData.page}
                    totalPages={blocksData.totalPages}
                    total={blocksData.total}
                    limit={PAGE_LIMIT}
                    onPageChange={setBlocksPage}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <UserFormDialog
        isOpen={editModal.isOpen}
        onClose={() => {
          editModal.close();
          refetch();
        }}
        user={editModal.selectedItem}
      />
      <UserNotifyDialog
        isOpen={notifyModal.isOpen}
        onClose={notifyModal.close}
        user={notifyModal.selectedItem}
      />
      <UserMuteDialog
        isOpen={muteModal.isOpen}
        onClose={() => {
          muteModal.close();
          refetch();
        }}
        user={muteModal.selectedItem}
      />
      <UserBanDialog
        isOpen={banModal.isOpen}
        onClose={() => {
          banModal.close();
          refetch();
        }}
        user={banModal.selectedItem}
      />
      <UserDeleteDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        user={deleteModal.selectedItem}
        onDeleted={() => navigate('/users')}
      />
    </div>
  );
};

export default UserDetailView;
